import { ImagePlus, Paperclip } from "lucide-react";
import type * as React from "react";
import { cn } from "../../lib/cn";

/**
 * Composer.DropOverlay — the "Trascina per allegare" affordance shown over the
 * composer card while a file is being dragged onto it.
 *
 * Mounted inside `Composer.Root` (which is `position: relative`). It is purely
 * presentational + `pointer-events-none` so the underlying drag handlers on
 * the card keep receiving the drag/drop events. The fade-in is `motion-safe`
 * so `prefers-reduced-motion` users get an instant appearance.
 *
 * 🖥️ Desktop (Tauri v2): this only works because the window config sets
 * `dragDropEnabled: false` — otherwise wry's native handler swallows the OS
 * drop and the DOM `ondrop` never fires with file data. See the desktop
 * audit in the composer parity plan.
 */
export interface ComposerDropOverlayProps {
  /** True while a drag is hovering the composer card. */
  active: boolean;
  /** Override copy (defaults to the Italian "Trascina per allegare"). */
  label?: string;
  className?: string;
}

export const ComposerDropOverlay: React.FC<ComposerDropOverlayProps> = ({
  active,
  label = "Trascina per allegare",
  className,
}) => {
  if (!active) return null;
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2",
        "rounded-[14px] border-2 border-ando-orange/60 border-dashed bg-cream/70 backdrop-blur-[2px]",
        "motion-safe:animate-[fadeIn_160ms_ease-out]",
        className,
      )}
    >
      <div className="flex items-center gap-2 text-ando-orange">
        <ImagePlus size={20} />
        <Paperclip size={20} />
      </div>
      <span className="font-medium text-[12px] text-ink-2">{label}</span>
    </div>
  );
};
ComposerDropOverlay.displayName = "Composer.DropOverlay";
